import React, { useCallback, useEffect, useMemo } from "react";

import axios from "axios";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import {
  offerFreecopyColumns,
  offerFreecopyRow,
} from "./tableParams/offerFreecopyColumns";
import { GET_OFFER_USERS_FREECOPY } from "../../actions/ActionTypes";
import { fetchApi } from "../../actions/FetchActions";
import { formatHeaders } from "../../utils/Api";
import { getElementsByIndex } from "../../utils/data/Content";
import rootURL from "../../utils/RootURL";
import ExportCsvButton from "../analytics/charts/ExportCsvButton";
import DataTable from "../dataTable/DataTable";
import { userEditPath } from "../routes/routes";

import type { JSX } from "react";

import type { OfferFreecopyUserRow } from "./tableParams/offerFreecopyColumns.types";
import type { OfferListItem } from "../../api/OfferAPI.types";
import type { AppState } from "../../reducers";

interface OfferFreecopyTableProps {
  offerUuid: string;
}

function OfferFreecopyTable({
  offerUuid,
}: OfferFreecopyTableProps): JSX.Element {
  const { t } = useTranslation("editor-offers");
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const dispatch = useDispatch();

  const token = useSelector((state: AppState) => state.loginReducer.token);
  const offers = useSelector((state: AppState) => state.fetchReducer.offers);
  const { data, isFetching, isFetched, failed } = useSelector(
    (state: AppState) => state.fetchReducer.offer_users_freecopy,
  );

  const fetchOfferUsers = useCallback((): void => {
    if (!projectSlug || !offerUuid) return;

    dispatch(
      fetchApi({
        endpoint: `/api/editor_stats/v1/admin/offer/freecopies?offer_uuid=${offerUuid}`,
        type: GET_OFFER_USERS_FREECOPY,
        item: "offer_users_freecopy",
        headers: [{ key: "xx-ok-project", value: projectSlug }],
        token,
      }),
    );
  }, [dispatch, projectSlug, offerUuid, token]);

  useEffect(() => {
    fetchOfferUsers();
  }, [fetchOfferUsers]);

  const offer: OfferListItem | undefined = getElementsByIndex(
    offerUuid,
    offers,
    "uuid",
  ).at(0);
  const isSvod = offer?.type === "svod";

  const rowList = useMemo(
    (): OfferFreecopyUserRow[] =>
      // Le reducer garde les lignes de l'offre précédente sur un échec.
      failed ? [] : data.map((user) => offerFreecopyRow(user, t)),
    [data, failed, t],
  );

  const columnList = useMemo(
    () => offerFreecopyColumns({ t, projectSlug, isSvod }),
    [t, projectSlug, isSvod],
  );

  const getRowHref = useCallback(
    (row: OfferFreecopyUserRow): string | undefined =>
      userEditPath(projectSlug, row.user_uuid),
    [projectSlug],
  );

  // Toute la liste, côté serveur : sans la recherche ni les filtres.
  const handleFetchCsv = async (): Promise<string> => {
    const response = await axios({
      url: rootURL(
        `/api/editor_stats/v1/admin/offer/freecopies?export&offer_uuid=${offerUuid}`,
      ),
      method: "GET",
      headers: formatHeaders(token, [
        { key: "xx-ok-project", value: projectSlug },
      ]),
    });
    return String(response.data);
  };

  const canExport = isFetched && !isFetching && data.length > 0;

  return (
    <DataTable
      columns={columnList}
      data={rowList}
      emptyText={t("editor-analytics:noDatas")}
      getRowHref={getRowHref}
      isError={failed}
      isLoading={!isFetched || isFetching}
      onRetry={fetchOfferUsers}
      title={t("editor-analytics:users_title_freecopy")}
      urlSync={{ prefix: "freecopy" }}
      headerActions={
        <ExportCsvButton
          disabled={!canExport}
          fetchCsv={handleFetchCsv}
          fileName="offer_users_freecopy.csv"
        />
      }
    />
  );
}

export default OfferFreecopyTable;
